import FA from 'admin/components/atoms/FontAwesome';
import moment, { Moment } from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { QueryParamConfig, useQueryParams, withDefault } from 'use-query-params';
import {
  getExpiredBalances,
  getExpiredBalancesCSV,
} from '~/javascripts/admin/actions/transactions';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import { downloadCsvFile, toQueryParams } from '~/javascripts/admin/utils';
import Button from '../../atoms/Buttons';
import Headings from '../../atoms/Headings';
import UserPrivateMoneyNotAllowed from '../../atoms/UserPrivateMoneyNotAllowed';
import BreadCrumb from '../../molecules/BreadCrumb';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';
import FilterForm from './FilterForm';
import ListTable from './ListTable';

type ExpiredBalances = Awaited<ReturnType<typeof getExpiredBalances>>;

const FormWrapper = styled.div``;

const HeadingWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 20px 0 10px 0;
`;

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '取引管理', href: '', isCurrentPage: true },
  { value: '失効履歴', href: '', isCurrentPage: true },
];

const MomentParam: QueryParamConfig<Moment | null | undefined> = {
  encode: (value) => (value ? value.toISOString() : null),
  decode: (value) => (value && typeof value === 'string' ? moment(value) : null),
};

const queryParamConfigs = {
  from: withDefault(MomentParam, null),
  to: withDefault(MomentParam, null),
};

const TransactionsExpired = () => {
  const { id: privateMoneyId } = useAppSelector(selectUserPrivateMoney);
  const { user } = useAppSelector((state) => ({
    user: state.user,
  }));

  const [expiredBalances, setExpiredBalances] = useState<ExpiredBalances>();

  const isSelectedMoneyAllowed = user.isIssuerOf(privateMoneyId) || user.isServiceUser();

  const [query, setQuery] = useQueryParams({
    ...queryParamConfigs,
  });

  const fetchExpiredBalances = useCallback(
    (queryLink = '') => {
      if (privateMoneyId) {
        // pagination uses already created query string in prev / next parameter
        const queryString = !!queryLink
          ? queryLink
          : toQueryParams({
              private_money_id: privateMoneyId,
              per_page: 50,
              ...query,
            });

        getExpiredBalances(queryString).then((res) => setExpiredBalances(res));
      }
    },
    [query, privateMoneyId]
  );

  const fetchExpiredBalancesCSV = () => {
    if (!privateMoneyId) return;

    getExpiredBalancesCSV(
      toQueryParams({
        private_money_id: privateMoneyId,
        perPage: 10000,
        maxPage: 30,
        ...query,
      })
    ).then((ret) => {
      downloadCsvFile(ret.url);
    });
  };

  const onChangePage = (paginatedQuery: string | null) =>
    fetchExpiredBalances(paginatedQuery ?? undefined);

  useEffect(() => {
    fetchExpiredBalances();
  }, [query, privateMoneyId]);

  return (
    <FormWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      {isSelectedMoneyAllowed ? (
        <>
          <SelectedPrivateMoneyDisplay />
          <FilterForm query={query} setQuery={setQuery} />
          <HeadingWrapper>
            <Headings.h5>検索結果</Headings.h5>
            <Button onClick={fetchExpiredBalancesCSV} variant="muted-outline">
              <FA icon="download" /> CSV
            </Button>
          </HeadingWrapper>
          <ListTable balance={expiredBalances} onChangePage={onChangePage} />
        </>
      ) : (
        <UserPrivateMoneyNotAllowed />
      )}
    </FormWrapper>
  );
};

export default TransactionsExpired;
